import { createSelector } from '../utils/selector-utils'
import {
  getUserSelectableVitalTypes,
  getVitalTypesBaseArray,
} from '../vital-types'

/**
 * @param {UserSelectableVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionIgnoreVitalType = id => ({
  [`trackedVitalTypes.${id}.ignored`]: true,
})

/**
 * @param {UserSelectableVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionUnIgnoreVitalType = id => ({
  [`trackedVitalTypes.${id}.ignored`]: null,
})

/**
 * @param {UserSelectableVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionSelectVitalType = id => ({
  [`trackedVitalTypes.${id}.selected`]: true,
})

/**
 * @param {UserSelectableVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionUnselectVitalType = id => ({
  [`trackedVitalTypes.${id}.selected`]: false,
})

/**
 * @param {UserSelectableVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionResumeVitalType = id => ({
  [`trackedVitalTypes.${id}.selected`]: null,
})

/**
 * @param {EmittableVitalType | IndividualVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionClearReceivedVitalType = id => ({
  [`trackedVitalTypes.${id}.hasReceivedAutoValue`]: null,
})

/**
 * @param {EmittableVitalType | IndividualVitalType} id The vital type ID
 * @returns {any} The changes to apply to the case.
 */
export const actionHasReceivedVitalType = id => ({
  [`trackedVitalTypes.${id}.hasReceivedAutoValue`]: true,
})

/**
 * @param {CaseData} caseData
 * @returns {CaseData['trackedVitalTypes']}
 */
export const selectTrackedVitalTypesRaw = caseData =>
  caseData.trackedVitalTypes || {}

const getVitalSelector = condition =>
  createSelector(selectTrackedVitalTypesRaw, vitalTypes =>
    Object.keys(vitalTypes).filter(key => condition(vitalTypes[key]))
  )

/** @type {(caseData: CaseData) => VitalType[]} } */
export const selectAutoVitalsTypesReceived = getVitalSelector(
  val => val && val.hasReceivedAutoValue
)

/** @type {(caseData: CaseData) => VitalType[]} } */
export const selectVitalsTypesSelectedNotAuto = getVitalSelector(
  val => val && val.selected && !val.hasReceivedAutoValue
)

/**
 * @param {{
 *   hasAutoData: boolean
 *   hasManualData: boolean
 *   ignored: boolean
 *   selected: boolean
 *   wasUnselected: boolean
 * }} param0
 * @returns {UserSelectableVitalTypeStatus}
 */
const determineStatus = ({
  hasAutoData,
  hasManualData,
  ignored,
  selected,
  wasUnselected,
}) => {
  if (ignored) {
    return 'ignored'
  }

  if (hasAutoData || hasManualData) {
    if (wasUnselected) {
      return 'paused'
    } else {
      return hasAutoData ? 'auto' : 'manual'
    }
  }

  // has no data
  return selected ? 'selected' : 'unselected'
}

/**
 * @param {UserSelectableVitalTypeStatus} status
 * @returns {boolean}
 */
const isPlaced = status =>
  status === 'auto' || status === 'selected' || status === 'manual'

const selectVitalTypesWithManualValues = caseData => {
  if (!caseData || !caseData.vitalRecords) {
    return {}
  }

  const types = {}

  for (const cpId in caseData.vitalRecords) {
    for (const typeId in caseData.vitalRecords[cpId].vitals) {
      types[typeId] = true
    }
  }
  return types
}

/**
 * @type {CaseSelector<{
 *   [key: string]: UserSelectableVitalTypeStatus
 * }>}
 */
export const selectVitalsTypesWithStatus = createSelector(
  selectTrackedVitalTypesRaw,
  selectAutoVitalsTypesReceived,
  selectVitalTypesWithManualValues,
  (vitalTypes, autoVitalsTypesReceived, typesWithManualValues) => {
    /**
     * @type {{
     *   [key: VitalType]: UserSelectableVitalTypeStatus
     * }}
     */
    const result = {}

    // Build a set of all ids, since we want to report on all of them
    // event if they aren't in tracked vital types raw yet
    const allIds = new Set([
      ...Object.keys(vitalTypes),
      ...autoVitalsTypesReceived,
      ...Object.keys(typesWithManualValues),
    ])

    // BP is special, as we want to report on the BP status, not the individual entries
    // but values are stored on the individual entries, while user selection/ignoring happens on bp
    if (allIds.has('bp') || allIds.has('sbp') || allIds.has('dbp')) {
      result.bp = determineStatus({
        // These are set by the user on the bp vital type directly
        ignored: vitalTypes.bp && vitalTypes.bp.ignored,
        selected: vitalTypes.bp && vitalTypes.bp.selected,
        wasUnselected: !!(vitalTypes.bp && vitalTypes.bp.selected === false),

        // But records are recorded to individual types
        hasAutoData:
          (vitalTypes.sbp && vitalTypes.sbp.hasReceivedAutoValue) ||
          (vitalTypes.dbp && vitalTypes.dbp.hasReceivedAutoValue),
        hasManualData: typesWithManualValues.sbp || typesWithManualValues.dbp,
      })
    }

    for (const id of Array.from(allIds)) {
      // handled above so don't add duplicate entries
      if (id === 'bp' || id === 'sbp' || id === 'dbp' || id === 'map') {
        continue
      }

      const found = vitalTypes[id]
      const hasReceivedAutoValue = autoVitalsTypesReceived.includes(id)
      const ignored = !!(found && found.ignored)
      const selected = !!(found && found.selected)
      const wasUnselected = !!(found && found.selected === false)

      result[id] = determineStatus({
        hasAutoData: hasReceivedAutoValue,
        hasManualData: !!typesWithManualValues[id],
        ignored,
        selected,
        wasUnselected,
      })
    }

    return result
  }
)

/** @type {(caseData: CaseData) => string[]} } */
export const selectAutoVitalsTypeIdsToReport = createSelector(
  selectVitalsTypesWithStatus,
  types => Object.keys(types).filter(key => types[key] === 'auto')
)

/** @type {(caseData: CaseData) => VitalType[]} } */
export const selectIgnoredVitalTypes = getVitalSelector(
  val => val && val.ignored
)

export const selectVitalTypesForMacro = createSelector(
  selectVitalsTypesSelectedNotAuto,
  selectIgnoredVitalTypes,
  (selected, ignored) => {
    const result = {}
    for (const id of selected) {
      result[id] = 'selected'
    }
    for (const id of ignored) {
      result[id] = 'ignored'
    }
    return result
  }
)

/**
 * @param {CaseData} caseData
 * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
 * @returns {DecoratedUserSelectableVitalType[]}
 */
export const optionSelectUserSelectableVitalTypes = (caseData, opts) => {
  const vitalsTypesWithStatus = selectVitalsTypesWithStatus(caseData)
  const monitorTypes = getUserSelectableVitalTypes(opts)
  return monitorTypes.map(monitorType => {
    const status = vitalsTypesWithStatus[monitorType.id] || 'unselected'
    return {
      ...monitorType,
      status,
      placed: isPlaced(status),
    }
  })
}

/**
 * @param {CaseData} caseData
 * @param {Pick<ReportRenderingInputs, 'tempUnit'>} opts
 * @returns {DecoratedIndividualVitalType[]}
 */
export const optionSelectIndividualVitalTypes = (caseData, opts) => {
  const vitalsTypesWithStatus = selectVitalsTypesWithStatus(caseData)
  const monitorTypes = getVitalTypesBaseArray(opts)
  const bpStatus = vitalsTypesWithStatus.bp || 'unselected'
  /** @type {DecoratedIndividualVitalType[]} */
  const toReturn = []
  for (const vital of monitorTypes) {
    // we skip this, we want them individually for this
    if (vital.id === 'bp') {
      continue
    }
    // for bp sub items we defer to status of bp
    const isBpSubItem =
      vital.id === 'sbp' || vital.id === 'dbp' || vital.id === 'map'

    const status = isBpSubItem
      ? bpStatus
      : vitalsTypesWithStatus[vital.id] || 'unselected'

    toReturn.push({
      ...vital,
      status,
      placed: isPlaced(status),
    })
  }
  return toReturn
}
